import { createColumnHelper, flexRender, getCoreRowModel, getPaginationRowModel, getSortedRowModel, SortingState, useReactTable } from "@tanstack/react-table";
import { useState } from "react";
import { BsFillArrowDownCircleFill } from "react-icons/bs";
import { FaCaretDown, FaCaretUp, FaAngleDoubleLeft, FaAngleDoubleRight } from "react-icons/fa";
import { MdDownload, MdOpenInNew } from "react-icons/md";
import Text from "../../../components/text";
import { CheckLabel, CheckOption, ContainerSelecao, DownloadSelectedFilesButton } from "../styles";
import { IDocumento } from "../../../@interfaces";
import { useTheme } from "styled-components";
import useIsMobile from "../../../hooks/useIsMobile";

interface IProps{
  listaDocumentos: IDocumento[],
  baixarArquivo: (id: number) => void,
  abrirDocumento: (id: number) => void,
  acaoCarregando: number | null
}

const TabelaDocumentos: React.FC<IProps> = ({ listaDocumentos, baixarArquivo, abrirDocumento, acaoCarregando }) => {
  const columnHelper = createColumnHelper<IDocumento>();
  const { colors } = useTheme();

  const BUTTON_STYLE = "bg-transparent border-none cursor-pointer"
  const colunas = [
    columnHelper.display({
      id: 'selecionar',
      enableSorting: false,
      header: '', 
      cell: ({ row }) => (
        <input type="checkbox" checked={row.getIsSelected() } onChange={row.getToggleSelectedHandler()} />
      ),
    }),
    columnHelper.accessor('descricaoArquivo', { header: 'Nome', sortingFn: 'text' }),
    columnHelper.accessor('nomeEmpresa', { header: 'Empresa', sortingFn: 'text' }),
    columnHelper.accessor('rotuloTipo', { header: 'Tipo', sortingFn: 'text' }),
    columnHelper.display({
      id: 'acoes',
      enableSorting: false,
      header: '',
      cell: ({ row }) => (
        <div className="flex items-center justify-end gap-2">
          <button onClick={() => baixarArquivo(row.original.arquivo)} className={BUTTON_STYLE}>
            {acaoCarregando === row.original.arquivo ? 
              <div className="h-4 w-4 animate-spin rounded-full border-2 !border-gray-200 border-solid !border-t-gray-600" />
              : <MdDownload color={colors.halfOpacityBlack} size={20} />}
          </button>
          <button onClick={() => abrirDocumento(row.original.arquivo)} className={BUTTON_STYLE}>
            <MdOpenInNew color={colors.halfOpacityBlack} size={20} />
          </button>
        </div>
      ),
    }),
  ];

  const [sorting, setSorting] = useState<SortingState>([]);

  const table = useReactTable({
    data: listaDocumentos,
    columns: colunas,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(), 
    initialState: {
      pagination: { pageSize: 10 },
    }

  });


  function iconeOrdenacao(ordenacao: false | 'asc' | 'desc') {
    if (ordenacao === 'asc') return <FaCaretUp />;
    if (ordenacao === 'desc') return <FaCaretDown />;
    return null;
  }


  async function baixarArquivosSelecionados(){
    const documentosSelecionados = table.getSelectedRowModel().rows.map(row => row.original);
    for (const documento of documentosSelecionados) {
      baixarArquivo(documento.arquivo);
    }
  }

  const isMobile = useIsMobile();

  return(
    <>
    <ContainerSelecao selecionarTodos={table.getIsAllRowsSelected()}>
      <CheckOption>
        <input
          className="cursor-pointer"
          type={"checkbox"}
          id="select_all"
          checked={table.getIsAllRowsSelected()}
          ref={(el) => {
            if (el) el.indeterminate = table.getIsSomeRowsSelected() && !table.getIsAllRowsSelected();
          }}
          onChange={table.getToggleAllRowsSelectedHandler()}
        />
        <CheckLabel
          htmlFor="select_all"
          style={{ color: colors.halfOpacityBlack }}
        >
          Selecionar todos
        </CheckLabel>
      </CheckOption>

      {(table.getIsSomeRowsSelected() || table.getIsAllRowsSelected()) && <DownloadSelectedFilesButton onClick={baixarArquivosSelecionados}>
        <Text className={`!w-fit !text-end !text-[.9rem] !font-semibold  !text-gray-600`}>{isMobile ? 'Baixar selecionados' : 'Baixar documentos selecionados'}</Text>
        <BsFillArrowDownCircleFill size={16} className="text-gray-600" />
      </DownloadSelectedFilesButton>}

    </ContainerSelecao>
    

    <table className="rounded overflow-hidden !text-[.85rem]">
      <thead>
        {table.getHeaderGroups().map(hg => (
          <tr key={hg.id}>
            {hg.headers.map(header => (
              <th 
                key={header.id} 
                onClick={header.column.getToggleSortingHandler()} 
                className={`whitespace-nowrap px-3 py-2 text-left font-medium bg-gray-600 text-gray-100 ${
                  header.column.getCanSort() ? 'cursor-pointer select-none hover:bg-gray-500' : ''
                }`}>

                <span className="flex items-center justify-between">
                  {flexRender(header.column.columnDef.header, header.getContext())}
                  {iconeOrdenacao(header.column.getIsSorted())}
                </span>
              </th>
            ))}
          </tr>
        ))}
      </thead>


      <tbody>
        {table.getRowModel().rows.map(row => (
          <tr key={row.id} className="bg-white">
            {row.getVisibleCells().map(cell => (
              <td key={cell.id} className="border-b border-solid border-gray-200 px-3 py-2">{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>

    <div className="flex items-center justify-between mt-4 w-[200px] self-end">
      <button
        onClick={() => table.previousPage()}
        disabled={!table.getCanPreviousPage()}
        className={BUTTON_STYLE}
      >
        <FaAngleDoubleLeft />
      </button>

      <span>
        Página {table.getState().pagination.pageIndex + 1} de {table.getPageCount()}
      </span>

      <button
        onClick={() => table.nextPage()}
        disabled={!table.getCanNextPage()}
        className={BUTTON_STYLE}
      >
        <FaAngleDoubleRight />
      </button>
    </div>
  </>
  )
}


export default TabelaDocumentos;